import { useCallback } from 'react';
import { bugtracker, useIdentityContext } from '@99designs/design-services-common';
import { BRIEF_PAGE_NAME, sendAddToCartTrackingEvent } from '@99designs/tracking';
import { useBriefSubmittedMutation } from '../../graph';
import { hasOverflowFeatureFlag } from '../../lib/experiments';
import { useCart } from '../../lib/hooks';
import { BriefRequestFragment } from '../BriefForm/brief.generated';

export const useBriefSubmit = (locale: string) => {
    const { authorizationHeader, shopperId } = useIdentityContext();
    const [briefSubmittedMutation] = useBriefSubmittedMutation();
    const { isInCart } = useCart(locale, {
        authorizationHeader,
        shopperId,
    });

    return useCallback(
        async (request: BriefRequestFragment) => {
            try {
                const [requestInCart, isOverflowEnabled] = await Promise.all([
                    isInCart(request.id),
                    hasOverflowFeatureFlag(
                        request.expertService?.isOverflowEligible,
                        request.facts?.withDesignLive
                    ),
                ]);

                const { data: requestsData, errors } = await briefSubmittedMutation({
                    variables: {
                        requestId: request.id,
                        isOverflow: isOverflowEnabled,
                    },
                });

                if (errors && errors.length > 0) {
                    throw new Error(
                        `useBriefSubmit mutation failed: ${errors.map((e) => e.message).join('; ')}`
                    );
                }
                if (!requestsData) throw new Error(`useBriefSubmit: Failed to call to requests`);

                if (!requestInCart) {
                    sendTrackingEvent(request);
                }
            } catch (e) {
                const err = e instanceof Error ? e : new Error(String(e));
                bugtracker.notify(err, (event) => {
                    event.context = `Error during brief submission for requestId: ${request.id}.`;
                    event.addMetadata('requestId', { requestId: request.id });
                });
                throw err;
            }
        },
        [briefSubmittedMutation, isInCart]
    );
};

function sendTrackingEvent(request: BriefRequestFragment) {
    const productId = request.expertService?.serviceProduct.mpvId;
    const name = request.expertService?.title;
    const coreProductId = request.expertService?.serviceProduct.productKey;
    const price = request.expertService?.price?.listPrice.taxed;
    const currency = request.expertService?.price.currency;

    if (!productId || !name || !coreProductId || !price || !currency) {
        bugtracker.notify(
            new Error(
                `Could not send tracking event for requestId: ${request.id} - missing required fields`
            ),
            (event) => {
                event.addMetadata('tracking', {
                    requestId: request.id,
                    productId,
                    name,
                    coreProductId,
                    price,
                    currency,
                });
            }
        );
        return;
    }

    sendAddToCartTrackingEvent({
        pageName: BRIEF_PAGE_NAME,
        pageStage: 'Design',
        pageSection: 'Design Services',
        product_id: productId,
        name,
        core_product_id: coreProductId,
        price,
        currency,
    });
}
